import { MUISelect, MUITextField } from "@abb/arcadia-ui-react"
import { DropdownLOVType } from "@abb/arcadia-ui-react/build/components/select/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useDeviceFamilies } from "../../../../hooks/useDeviceFamilies"
import { digitalManagerApi } from "../../../../services/api"
import { FieldRow, FormPage } from "../../../components/FormPage"
import { DevicePost } from "../../../models/Device"
import { ControllerRendererProps } from "../../../types/reactHookForm"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  slaveId: z
    .number({ error: "Slave ID must be a number" })
    .int("Slave ID must be an integer")
    .min(0, "Slave ID is required"),
  familyId: z.string().min(1, "Device Family is required"),
})

const returnURL = `/devices`

export function DeviceCreate() {
  const { deviceFamiliesList, isLoadingDeviceFamilies } = useDeviceFamilies()
  const navigate = useNavigate()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<DevicePost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      name: "",
      slaveId: 0,
      familyId: "",
    })
  }, [reset])

  const onSubmit = useCallback(
    (values: DevicePost) => digitalManagerApi
        .post(`/devices`, {
          name: values.name,
          slaveId: values.slaveId,
          familyId: values.familyId,
        })
        .then((res) => {
          if (res.status === StatusCodes.CREATED) {
            toast.success("Device created")
            navigate(returnURL)
          }
        })
        .catch(() => {
          toast.error("Cannot create device")
        }),
    [navigate]
  )

  const handleNameFieldChange = useCallback(
    (field: ControllerRenderProps<DevicePost, "name">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: ControllerRendererProps<DevicePost, "name">) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleNameFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleNameFieldChange]
  )

  const handleChangeNumberField = useCallback(
    (value: string) => {
      const parsedValue = value ? Number.parseInt(value, 10) : 0
      setValue("slaveId", parsedValue, { shouldValidate: true, shouldDirty: true })
    },
    [setValue]
  )

  const renderSlaveIdTextField = useCallback(
    ({ field }: ControllerRendererProps<DevicePost, "slaveId">) => (
      <MUITextField
        id="slaveId"
        type="number"
        size={32}
        name={field.name}
        value={
          field.value !== undefined && field.value !== null ? String(field.value) : ""
        }
        onChange={handleChangeNumberField}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.slaveId?.message === "string"
            ? { slaveId: { message: errors.slaveId.message } }
            : undefined
        }
      />
    ),
    [errors.slaveId, handleChangeNumberField]
  )

  const handleFamilySelectChange = useCallback(
    (
      values: string[] | DropdownLOVType[],
      field: ControllerRenderProps<DevicePost, "familyId">
    ) => {
      const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
      const value = typeof first === "string" ? first : (first?.id ?? "")
      field.onChange(value)
    },
    []
  )

  const handleFamilySelectForField = useCallback(
    (field: ControllerRenderProps<DevicePost, "familyId">) =>
      (values: string[] | DropdownLOVType[]) => {
        handleFamilySelectChange(values, field)
      },
    [handleFamilySelectChange]
  )

  const renderFamilySelect = useCallback(
    ({ field }: ControllerRendererProps<DevicePost, "familyId">) => (
      <MUISelect
        name={field.name}
        id="familyId"
        size={32}
        dropdownLOV={deviceFamiliesList.map((d) => ({
          id: String(d.id),
          name: d.name ?? "",
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleFamilySelectForField(field)}
        onBlur={field.onBlur}
        isDisabled={isLoadingDeviceFamilies}
        errors={
          errors.familyId?.message
            ? { familyId: { message: String(errors.familyId.message) } }
            : undefined
        }
      />
    ),
    [
      deviceFamiliesList,
      handleFamilySelectForField,
      isLoadingDeviceFamilies,
      errors.familyId,
    ]
  )

  const renderFamilyErrorField = useCallback(() => {
    if (!errors.familyId) {
      return null
    }
    return (
      <MUITextField
        isDisabled
        value=""
        errors={{
          familyId: {
            message:
              typeof errors.familyId?.message === "string" ? errors.familyId.message : "",
          },
        }}
      />
    )
  }, [errors.familyId])

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Name">
        <Controller name="name" control={control} render={renderNameTextField} />
      </FieldRow>
      <FieldRow label="Slave ID">
        <Controller name="slaveId" control={control} render={renderSlaveIdTextField} />
      </FieldRow>
      <FieldRow label="Family">
        <>
          <Controller name="familyId" control={control} render={renderFamilySelect} />
          {renderFamilyErrorField()}
        </>
      </FieldRow>
    </FormPage>
  )
}
